
export function isValidIFSC(value) {
  if (!value || typeof value !== "string") return false;
  const v = value.trim().toUpperCase();
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v);
}

const digitOnly = (s) => /^\d+$/.test(s);

export function validateBillingForm(data) {
  const errors = {};

  if (!data.customerName?.trim()) {
    errors.customerName = "Customer name is required";
  }

  const phone = String(data.phone ?? "").trim();
  if (!phone) {
    errors.phone = "Phone is required";
  } else if (!digitOnly(phone) || phone.length !== 10) {
    errors.phone = "Phone must be exactly 10 digits";
  }

  // const emailBilling = String(data.email ?? "").trim();
  // if (!emailBilling) {
  //   errors.email = "Email is required";
  // } else {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   if (!emailRegex.test(emailBilling)) {
  //     errors.email = "Enter a valid email";
  //   }
  // }

  const accountNumberBilling = String(data.accountNumber ?? "").trim();
  if (accountNumberBilling && !digitOnly(accountNumberBilling)) {
    errors.accountNumber = "Account number must contain digits only";
  }

  const ifscBilling = String(data.ifsc ?? "").trim();
  if (ifscBilling && !isValidIFSC(ifscBilling)) {
    errors.ifsc = "Enter a valid 11-character IFSC";
  }

  const loan = parseFloat(data.loanAmount);
  if (data.loanAmount === "" || data.loanAmount == null || Number.isNaN(loan)) {
    errors.loanAmount = "Loan amount is required";
  } else if (loan <= 0) {
    errors.loanAmount = "Loan amount must be greater than 0";
  }

  const interest = parseFloat(data.interest);
  if (data.interest === "" || data.interest == null || Number.isNaN(interest)) {
    errors.interest = "Interest is required";
  } else if (interest < 0 || interest > 100) {
    errors.interest = "Interest must be between 0 and 100";
  }

  const processingFee = parseFloat(data.processingFeePercent);
  if (data.processingFeePercent === "" || data.processingFeePercent == null || Number.isNaN(processingFee)) {
    errors.processingFeePercent = "Processing fee % is required";
  } else if (processingFee < 0 || processingFee > 100) {
    errors.processingFeePercent = "Processing fee must be between 0 and 100";
  }

  const goldRaw = data.goldRate;
  const gold =
    typeof goldRaw === "number" && !Number.isNaN(goldRaw)
      ? goldRaw
      : parseFloat(String(goldRaw ?? "").trim());
  const goldMissing =
    goldRaw === "" ||
    goldRaw == null ||
    (typeof goldRaw === "string" && goldRaw.trim() === "") ||
    Number.isNaN(gold);
  if (goldMissing) {
    errors.goldRate = "Gold rate is required";
  } else if (gold <= 0) {
    errors.goldRate = "Gold rate must be greater than 0";
  }

  if (!data.startDate) {
    errors.startDate = "Start date is required";
  }
  if (!data.endDate) {
    errors.endDate = "End date is required";
  }
  if (data.startDate && data.endDate) {
    const s = new Date(`${data.startDate}T00:00:00`);
    const e = new Date(`${data.endDate}T00:00:00`);
    if (s >= e) {
      errors.endDate = "End date must be after start date";
    }
  }

  const ornaments = Array.isArray(data.ornaments) ? data.ornaments : [];
  ornaments.forEach((row, i) => {
    const parseNonNeg = (key, raw) => {
      if (raw === "" || raw == null) return null;
      const n = parseFloat(raw);
      if (Number.isNaN(n)) {
        errors[`${key}_${i}`] = "Invalid number";
        return null;
      }
      if (n < 0) {
        errors[`${key}_${i}`] = "Cannot be negative";
        return null;
      }
      return n;
    };

    parseNonNeg("units", row.units);
    parseNonNeg("net", row.net);
  });

  const isValid = Object.keys(errors).length === 0;
  return { isValid, errors };
}

export function validateCustomerForm(data) {
  const errors = {};

  if (!data.customerName || String(data.customerName).trim() === "") {
    errors.customerName = "Customer name is required";
  }

  const address = String(data.address ?? "").trim();
  if (!address) {
    errors.address = "Address is required";
  }

  const phone = String(data.phone ?? "").trim();
  if (!phone) {
    errors.phone = "Phone is required";
  } else if (!digitOnly(phone) || phone.length !== 10) {
    errors.phone = "Phone must be exactly 10 digits";
  }

  // const email = String(data.email ?? "").trim();
  // if (!email) {
  //   errors.email = "Email is required";
  // } else {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   if (!emailRegex.test(email)) {
  //     errors.email = "Enter a valid email";
  //   }
  // }

  const accountNumber = String(data.accountNumber ?? "").trim();
  if (accountNumber && !digitOnly(accountNumber)) {
    errors.accountNumber = "Account number must contain digits only";
  }

  const ifsc = String(data.ifsc ?? "").trim();
  if (ifsc && !isValidIFSC(ifsc)) {
    errors.ifsc = "Enter a valid 11-character IFSC";
  }

  if (!data.profilePicture) {
    errors.profilePicture = "Profile picture file is required";
  }

  if (!data.aadharCard) {
    errors.aadharCard = "Aadhar card file is required";
  }

  if (!data.panCard) {
    errors.panCard = "PAN card file is required";
  }

  const isValid = Object.keys(errors).length === 0;
  return { isValid, errors };
}

export function validateCreateUserForm(data) {
  const errors = {};

  if (!data.email || String(data.email).trim() === "") {
    errors.email = "Email is required";
  } else {
    const em = String(data.email).trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(em)) {
      errors.email = "Enter a valid email";
    }
  }

  if (!data.password || String(data.password).trim() === "") {
    errors.password = "Temporary password is required";
  } else if (String(data.password).trim().length < 6) {
    errors.password = "Password must be at least 6 characters";
  }

  if (!data.role || String(data.role).trim() === "") {
    errors.role = "Role is required";
  }

  const isValid = Object.keys(errors).length === 0;
  return { isValid, errors };
}

export function validateLoginForm(data) {
  const errors = {};

  if (!data.email || String(data.email).trim() === "") {
    errors.email = "Email is required";
  } else {
    const em = String(data.email).trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(em)) {
      errors.email = "Enter a valid email";
    }
  }

  if (!data.pass || String(data.pass).trim() === "") {
    errors.pass = "Password is required";
  }

  const isValid = Object.keys(errors).length === 0;
  return { isValid, errors };
}
